import React, { createContext, useContext, useEffect, useState, useRef } from 'react';
import { auth } from '../lib/firebase';
import { signInAnonymously, onAuthStateChanged } from 'firebase/auth';
import type { User } from 'firebase/auth';

interface AuthContextType {
  user: User | null;
  isAdmin: boolean;
  loading: boolean;
  error: string | null;
}

const AuthContext = createContext<AuthContextType>({
  user: null,
  isAdmin: false,
  loading: true,
  error: null,
});

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const isSigningIn = useRef(false);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (userState) => {
      if (userState) {
        try {
          // Force refresh to get the latest custom claims.
          const idTokenResult = await userState.getIdTokenResult(true);
          setIsAdmin(!!idTokenResult.claims.admin);
          setUser(userState);
          setError(null); // Clear any previous errors
        } catch (err: any) {
          console.warn('Fehler beim Laden der Claims:', err);
          // Setze den User trotzdem, auch wenn Claims nicht geladen werden können
          setUser(userState);
          setIsAdmin(false);
        } finally {
          setLoading(false);
          isSigningIn.current = false;
        }
      } else if (!isSigningIn.current) {
        // Nur anmelden, wenn wir es nicht bereits versuchen
        isSigningIn.current = true;
        try {
          console.log('Starte anonyme Anmeldung...');
          const result = await signInAnonymously(auth);
          console.log('Anonyme Anmeldung erfolgreich:', result.user.uid);
          setUser(result.user);
          setError(null);
        } catch (err: any) {
          console.error('Fehler bei anonymer Anmeldung:', err);
          setError(`Authentifizierungsfehler: ${err.message}`);
          setUser(null);
        } finally {
          setLoading(false);
          isSigningIn.current = false;
        }
      }
    });

    return () => unsubscribe();
  }, []);

  return (
    <AuthContext.Provider value={{ user, isAdmin, loading, error }}>
      {children}
    </AuthContext.Provider>
  );
}; 